import { useState, useEffect, useRef, MouseEvent } from 'react';
import IndividualType from "../types/individual";
import { IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BrokenImage, Restore, ZoomIn } from '@mui/icons-material';
import { TILE_SIZE } from '../config/constants';
import { useQuery } from 'react-query';
import useApiClient from '../hooks/useApiClient';
import { Node } from '../classes/CircularLinkedList';
import { CSSTransition } from 'react-transition-group';
import './Individual.css';
import { AxiosError } from 'axios';
import useUserData from '../hooks/useUserData';

interface IndividualProps extends IndividualType {
  toggleSelected: (id: string) => void;
  setImageUrl: (id: string, imageUrl: string | undefined) => void;
  setGenerationError: (id: string) => void;
  showZoom: (node: Node<IndividualType> | null) => void;
  carouselNode: Node<IndividualType> | null;
  isFocused: boolean; // for dotted line highlight for keyboard operation
  refreshUsage: () => void;
}

export default function Individual({
  id,
  selected,
  genome,
  imageUrl,
  generationError = false,
  setImageUrl,
  setGenerationError,
  toggleSelected,
  showZoom,
  carouselNode,
  isFocused,
  refreshUsage,
}: IndividualProps) {
  const { apiClient } = useApiClient()

  const [showZoomButton, setShowZoomButton] = useState(false);
  const [isMobileLongPress, setIsMobileLongPress] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isImageError, setIsImageError] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const theme = useTheme();

  const { apiKey } = useUserData();
  
  useEffect(() => {
    if (isMobileLongPress) {
      setShowZoomButton(true);
    }
  }, [isMobileLongPress]);

  const handleMouseOver = () => {
    setShowZoomButton(true);
  };

  const handleMouseOut = () => {
    setShowZoomButton(false);
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    // Ignore "right"-clicks
    if (event.button !== 0) return;

    clearTimer();
    timerRef.current = setTimeout(() => {
      setIsMobileLongPress(true);
    }, 500); // 500ms for long press
  };

  const handleMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    // Ignore "right"-clicks
    if (event.button !== 0) return;

    clearTimer();
    if (!isMobileLongPress) {
      toggleSelected(id);
    } else {
      setIsMobileLongPress(false);
    }
  };

  // cancel longpress when losing browser tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && timerRef.current) {
        clearTimeout(timerRef.current);
        setIsMobileLongPress(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchImage = async () => {
    const { data, status, statusText } = await apiClient.post('/image', {
      prompt: genome,
      apiKey,
    });

    if (status !== 200)
      throw new Error(`HTTP error: ${status} (${statusText})`);

    const imageUrl = data.imageUrl as string;

    if (imageUrl && imageUrl.match(/^http/))
      return imageUrl;
    else
      throw new Error("missing data from /image");
  };

  //  const fetchMockImage = async () => {
  //    await new Promise(resolve => setTimeout(resolve, 2000));
  //    return 'https://pbs.twimg.com/profile_images/2353144645/kffet08kyqzg0elhyira_400x400.jpeg';
  //  }

  const imageQuery = useQuery({
    queryKey: ['image', id, genome, apiKey],
    enabled: !!genome && !imageUrl && !generationError,
    queryFn: fetchImage,
    retry: (failureCount, error) => {
      // don't retry 400 errors ('Bad request' - possible content violations...)
      // or for 402 errors ('Payment required' - free quota exceeded)
      if (error instanceof AxiosError &&
	  (error.response?.status === 400 ||
	   error.response?.status === 402))
        return false;
      // Retry for other errors, including rate-limit exceeding
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (imageQuery.data && !imageUrl) {
      setImageUrl(id, imageQuery.data);
      refreshUsage();
    } else if (imageQuery.error)
      setGenerationError(id);
  }, [imageQuery.data, imageUrl, setImageUrl, id, imageQuery.error, setGenerationError, refreshUsage]);

  // effect to handle image loading and error
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      const handleLoad = () => {
        setIsImageLoaded(true);
      };
      img.onload = handleLoad;
      img.onerror = () => {
        setIsImageError(true);
      };
      img.src = imageUrl;

      // Check if the image is already cached
      if (img.complete) {
        handleLoad();
      }
    }
  }, [imageUrl]);

  const handleRegenerateImage = () => {
    setIsImageError(false);
    setIsImageLoaded(false);
    setImageUrl(id, undefined);
  };

  const nodeRef = useRef(null);

  const borderColor = selected ? theme.palette.primary.main : (isFocused ? '#aaa' : 'transparent');
  const borderStyle = isFocused ? 'dashed' : 'solid';
  const borderWidth = '4px';

  return (
    <div
      className='individual'
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      style={{
        borderColor,
        borderStyle,
        borderWidth,
        padding: '4px',
      }}
    >

      <div className="image-container" style={{ position: 'relative', width: TILE_SIZE, height: TILE_SIZE }}>
        {genome && imageUrl ?
          isImageError ? ( // Show IconButton if image fails to load
            <Tooltip title="The image is no longer available. Click to regenerate. Note that it will use a different random seed.">
              <IconButton
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent the event from bubbling up
                }}
                onMouseUp={(e) => {
                  e.stopPropagation(); // Prevent the event from bubbling up
                  handleRegenerateImage();
                }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Restore />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <CSSTransition
                nodeRef={nodeRef}
                in={isImageLoaded}
                timeout={1000}
                classNames="fade"
                unmountOnExit
              >
                <img src={imageUrl} ref={nodeRef} alt={genome} style={{ width: '100%', height: '100%' }} />
              </CSSTransition>
              {showZoomButton && (
                <IconButton
                  size="small"
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the toggleSelected
                    showZoom(carouselNode);
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    clearTimer();
                  }}
                >
                  <ZoomIn />
                </IconButton>
              )}
            </>
          )
          : generationError ?
            (
              <div
                title="Sorry. A problem occurred with image generation."
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <BrokenImage />
              </div>
            )
            :
            (
              <div
                title={imageQuery.isLoading ? 'generating image...' : genome ? 'ready to generate image' : 'thinking about the next image...'}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
              >
                <CircularProgress
                  color={imageQuery.isLoading ? 'success' : genome ? 'warning' : 'info'}
                />
              </div>
            )}
      </div>
    </div>
  );
}
